import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Form } from '@components/atoms/Form';
import { useData } from '@hooks/useData';
import { useDeleteAccount } from '@services/useDeleteAccount';
import { useNavigate } from 'react-router-dom';
import { RoundProfilePicture } from '@components/atoms/RoundProfilePicture';
import { Space } from '@components/atoms/Space';
import styles from './DeleteAccountForm.module.css';
import { Button, ButtonMode } from '@components/atoms/Button';

export const DeleteAccountForm: FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { request: requestDeleteAccount, loading, lastResponseTime } = useDeleteAccount();
    const { login } = useData();

    const translations = {
        delete: t('user-area.delete'),
        yes: t('common.yes'),
        no: t('common.no'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleOnClickLogout = () => {
        setShowConfirm(true); // open confirmation
    };

    const handleConfirmYes = () => {
        formRef?.current?.requestSubmit(); // submit deletion
        setShowConfirm(false);
    };

    const handleConfirmNo = () => {
        setShowConfirm(false); // just close
    };

    const submitForm = () => {
        requestDeleteAccount();
    };

    // if logged out successfully, go to login page
    useEffect(() => {
        if (lastResponseTime !== null && login === null) {
            navigate('/login');
        }
    }, [lastResponseTime, login, navigate]);

    return (
        <Form formRef={formRef} classes="d-flex flex-column" onSubmit={submitForm}>
            <span
                onClick={loading ? undefined : handleOnClickLogout}
                style={{
                    cursor: loading ? 'default' : 'pointer',
                    opacity: loading ? 0.5 : 1,
                }}>
                <Typography fontSize="16" fontWeight="300" color="dark">
                    <>
                        <Typography fontWeight="300" tagVariant={TypographyTagVariants.span}>
                            <i style={{ marginRight: '5px' }} className="fa-thin fa-trash-can"></i>
                        </Typography>
                        {` ${translations.delete}`}
                    </>
                </Typography>
            </span>
            <>
                {showConfirm && (
                    <div className={styles.overlay}>
                        <div className={styles.pickerContainer}>
                            <RoundProfilePicture
                                icon="fa-thin fa-triangle-exclamation"
                                labelText={t('user-area.delete-account')}
                                hintText={t('user-area.delete-warning')}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                            <Button
                                label={translations.yes}
                                mode={ButtonMode.primary}
                                classes={styles.button}
                                onClick={handleConfirmYes}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            <Button
                                label={translations.no}
                                mode={ButtonMode.primary}
                                outline
                                classes={styles.button}
                                onClick={handleConfirmNo}
                            />
                        </div>
                    </div>
                )}
            </>
        </Form>
    );
};
